import { Avatar, Text } from '@capsicum/common-web';
import { HeartFilledIcon } from '@capsicum/common-web/icons';
import { type ComponentProps, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

type TeamProps = ComponentProps<'div'> & {
	disabled?: boolean;
	image?: string;
	isFavourite?: boolean;
	isSmall?: boolean;
	name: string;
};

export const Team = ({ disabled = false, image, isFavourite = false, isSmall = false, name, ...props }: TeamProps) => {
	const {
		colors: {
			icons: {
				brand: { enabled: enabledColor }
			}
		},
		sizing: { xxsmall: xxsmallSize }
	} = useTheme();

	const floatingIcon = useMemo(() => {
		return isFavourite
			? () => (
					<S.IconWrapper $isSmall={isSmall}>
						<S.CustomHeartFilledIcon color={enabledColor} height={xxsmallSize} width={xxsmallSize} />
					</S.IconWrapper>
				)
			: undefined;
	}, [enabledColor, isFavourite, isSmall, xxsmallSize]);

	return (
		<S.Wrapper {...props}>
			<S.StyledAvatarContainer $disabled={disabled}>
				<Avatar
					floatingIcon={floatingIcon}
					floatingIconConfig={{ bordered: false, position: 'end', size: 26 }}
					size={isSmall ? 'S' : 'M'}
					src={image}
					testID={`selectFavouriteTeam-favouriteTeamAvatar-${name}`}
				/>
			</S.StyledAvatarContainer>

			<S.TeamName $disabled={disabled} variant="caption1">
				{name}
			</S.TeamName>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		align-items: center;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const StyledAvatarContainer = styled.div<{ $disabled: boolean }>`
		opacity: ${({ $disabled, theme }) => ($disabled ? theme.opacity._60 : 1)};
	`;

	export const TeamName = styled(Text)<{ $disabled: boolean }>`
		overflow: hidden;
		text-align: center;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};
	`;

	export const IconWrapper = styled.div<{ $isSmall: boolean }>`
		display: flex;
		align-items: center;
		width: ${({ theme }) => theme.spacings.medium};
		height: ${({ theme }) => theme.spacings.medium};
		margin: ${({ $isSmall, theme }) =>
			`${$isSmall ? theme.spacings.xxsmall : theme.spacings.xxxsmall} 0 0 ${$isSmall ? theme.spacings.xxsmall : theme.spacings.xxxsmall}`};
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const CustomHeartFilledIcon = styled(HeartFilledIcon)`
		flex: 1;
	`;
}
